/**
 * DCTA-124 — which cards in the chat are live, and which a new card retires.
 *
 * One live card at a time (README deviations, item 5): a new request's card retires every live
 * card — it stays in the stream, collapsed, and is cancelled at the bank so it can never be signed.
 *
 * One exception (DCTA-123 (oo)): an offer on a refused step BESIDE a signable card is scoped to that
 * step. Accepting it makes a new card holding that step alone and leaves the first card untouched at
 * the bank — so the first card stays live here too, beside the new one. Each card signs its own
 * bundle (the chat binds every card's buttons to its own entry, never to "the newest").
 *
 * "Live" here means "still in play"; whether a card can be signed right now is the card's own state.
 */
export class LiveCards<T> {
  #newest: T | undefined;
  readonly #beside = new Set<T>();

  /** The card drawn last. */
  get newest(): T | undefined {
    return this.#newest;
  }

  /** Every live card, oldest first. */
  all(): T[] {
    return [...this.#beside, ...(this.#newest === undefined ? [] : [this.#newest])];
  }

  alive(card: T): boolean {
    return card === this.#newest || this.#beside.has(card);
  }

  /**
   * A new card is drawn. `scoped`: it answers a scoped offer — nothing is retired and the cards on
   * screen stay live beside it. Otherwise every live card is returned, for the chat to retire.
   */
  add(card: T, scoped: boolean): T[] {
    if (scoped) {
      if (this.#newest !== undefined) this.#beside.add(this.#newest);
      this.#newest = card;
      return [];
    }
    const retired = this.all();
    this.#beside.clear();
    this.#newest = card;
    return retired;
  }

  /** A request answered with no card (a question, a refusal, a read): every live card is returned to retire. */
  clear(): T[] {
    const out = this.all();
    this.#beside.clear();
    this.#newest = undefined;
    return out;
  }

  /** One card leaves on its own (paused at the scam question); the others stay. */
  drop(card: T): void {
    if (this.#newest === card) this.#newest = undefined;
    this.#beside.delete(card);
  }
}
